import { FC, useCallback, useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { MAX_RATE_PHOTOS, Ratio, TicketingFormCategory } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";
import { v4 } from "uuid";

import { useRateContext } from "../../../Context/RateContext";

import { useTranslate } from "@/common/hooks/useTranslate";
import { ImageUploader } from "@/components/ImageUploader/ImageUploader";
import { ImageUploadContext, ImageUploaderHandle } from "@/components/ImageUploader/types";
import { zeffyCardClasses } from "@/dashboard-design-system/Card/types";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useRateImageEditors } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/RateImageEditors/RateImageEditorsContext";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";
import { Cross as Close, Add, ChevronLeft, ChevronRight } from "@/icons/outlined";

const CAROUSEL_SIZE = 184;

type SlideDirection = "left" | "right";

export const RateImagesCarousel: FC = () => {
  const { t } = useTranslate();
  const { rateId, rateIndex, formCategory } = useRateContext();
  const form = useFormContext<StepTicketsFormData>();
  const { setValue, getValues } = form;
  const imageEditors = useRateImageEditors();

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const slideDirectionRef = useRef<SlideDirection | null>(null);
  const uploaderRef = useRef<ImageUploaderHandle>(null);

  const isCustom = formCategory === TicketingFormCategory.Custom;

  const ratePhotoUrlsKey = `rates.${rateIndex}.photos` as const;
  const values = useWatch({ control: form.control, name: ratePhotoUrlsKey }) || [{ id: v4() }];

  const firstImageUploaded = Boolean(values[0]?.photoUrl);
  const hasMultipleImages = values.length > 1;
  const canAddMoreImages = values.length < MAX_RATE_PHOTOS && firstImageUploaded;
  const currentValue = values[activeImageIndex];

  const finalizeActiveImage = useCallback(async () => {
    await uploaderRef.current?.finalizeSquareCrop();
  }, []);

  useEffect(() => {
    if (!imageEditors) {
      return;
    }
    imageEditors.registerImageEditor(rateId, finalizeActiveImage);
    return () => imageEditors.unregisterImageEditor(rateId);
  }, [imageEditors, rateId, finalizeActiveImage]);

  const addOneMoreImage = useCallback(() => {
    void finalizeActiveImage();
    const photoValues = getValues(ratePhotoUrlsKey);
    const newValues = [...(photoValues || []), { photoUrl: null, id: v4() }];
    setValue(ratePhotoUrlsKey, newValues, { shouldDirty: true });
    slideDirectionRef.current = "right";
    setActiveImageIndex(newValues.length - 1);
    window.setTimeout(() => uploaderRef.current?.openSelectImageModal(), 50);
  }, [finalizeActiveImage, getValues, ratePhotoUrlsKey, setValue]);

  const goToPreviousImage = useCallback(async () => {
    await finalizeActiveImage();
    slideDirectionRef.current = "left";
    setActiveImageIndex((previous) => previous - 1);
  }, [finalizeActiveImage]);

  const goToNextImage = useCallback(async () => {
    await finalizeActiveImage();
    slideDirectionRef.current = "right";
    setActiveImageIndex((previous) => previous + 1);
  }, [finalizeActiveImage]);

  const deleteImage = useCallback(
    (toDeleteIndex: number) => () => {
      const photoValues = getValues(ratePhotoUrlsKey);
      const newValues = photoValues?.filter((_, index) => index !== toDeleteIndex);
      const remainingValues = newValues && newValues?.length > 0 ? newValues : [{ photoUrl: null, id: v4() }];
      setValue(ratePhotoUrlsKey, remainingValues, { shouldValidate: true, shouldDirty: true });
      slideDirectionRef.current = null;
      setActiveImageIndex((previous) => Math.min(previous, remainingValues.length - 1));
    },
    [getValues, ratePhotoUrlsKey, setValue]
  );

  return (
    <Stack spacing={1.5} sx={{ width: CAROUSEL_SIZE }}>
      <Stack
        sx={{
          width: CAROUSEL_SIZE,
          height: CAROUSEL_SIZE,
          position: "relative",
          overflow: "hidden",
          [`& .${zeffyCardClasses.content}`]: { padding: 0 },
        }}
      >
        <Box
          key={currentValue?.id ?? activeImageIndex}
          sx={{
            width: "100%",
            height: "100%",
            "@keyframes slideFromRight": {
              from: { transform: "translateX(100%)" },
              to: { transform: "translateX(0)" },
            },
            "@keyframes slideFromLeft": {
              from: { transform: "translateX(-100%)" },
              to: { transform: "translateX(0)" },
            },
            animation: slideDirectionRef.current
              ? `${slideDirectionRef.current === "right" ? "slideFromRight" : "slideFromLeft"} 0.25s ease-out`
              : "none",
          }}
        >
          <ImageUploader
            ref={uploaderRef}
            data-test="rate-image-input"
            field={{
              form,
              name: `rates.${rateIndex}.photos.${activeImageIndex}.photoUrl`,
              isUploadingName: "imageIsUploading",
            }}
            editor={{ kind: "square", targetSize: 800, centerFit: true }}
            layout={{ ratio: Ratio.Square }}
            upload={{
              context: ImageUploadContext.LiveFormEditorRateImage,
              preset: "square_800",
              persistToAssetLibrary: false,
            }}
            library={isCustom ? { allowZeffyImages: true, allowUnsplash: true, defaultTab: "myImages" } : undefined}
            labels={{ import: t("dashboard", "ticketing.uploadImage") }}
            emptyState={{
              actions: [
                isCustom
                  ? { type: "selectFromLibrary", label: t("dashboard", "ticketing.uploadImage") }
                  : { type: "upload", label: t("dashboard", "ticketing.uploadImage") },
              ],
            }}
            showDeleteButton={false}
            onBeforeDelete={() => {
              deleteImage(activeImageIndex)();
              return false;
            }}
          />
        </Box>
        {currentValue?.photoUrl && (
          <DashboardIconButton
            onClick={deleteImage(activeImageIndex)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.95)" },
            }}
            vibe="neutral"
            size="small"
          >
            <Close />
          </DashboardIconButton>
        )}
      </Stack>

      {(canAddMoreImages || hasMultipleImages) && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {canAddMoreImages ? (
            <DashboardButton
              onClick={addOneMoreImage}
              vibe="neutral"
              size="small"
              variant="outlined"
              startIcon={<Add />}
            >
              {t("dashboard", "ticketing.imageLabel")}
            </DashboardButton>
          ) : (
            <span />
          )}

          {hasMultipleImages && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <DashboardIconButton
                onClick={goToPreviousImage}
                disabled={activeImageIndex === 0}
                vibe="neutral"
                size="small"
              >
                <ChevronLeft />
              </DashboardIconButton>
              <DashboardTypography variant="body2" sx={{ whiteSpace: "nowrap", textAlign: "center" }}>
                {t("dashboard", "ticketing.imageCount", { current: activeImageIndex + 1, total: values.length })}
              </DashboardTypography>
              <DashboardIconButton
                onClick={goToNextImage}
                disabled={activeImageIndex === values.length - 1}
                vibe="neutral"
                size="small"
              >
                <ChevronRight />
              </DashboardIconButton>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};
